import React, { FC, useCallback, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { DraggableWithHandle, move } from '@cld/draggable';
import { updateOptions } from '../../../../actions';
import { messages } from '../../../../i18n';
import { useOptions } from '../../../../selectors';
import { TextLine } from '../../common/TextLine';
import { CustomPreset } from './CustomPreset';
import { PredefinedPreset } from './PredefinedPreset';

export const DRAGGABLE_HANDLE_HEIGHT = 42;

const StyledDraggableWithHandle = styled(DraggableWithHandle)`
  height: ${DRAGGABLE_HANDLE_HEIGHT}px;
`;

const presetNameToLabelMessage = {
  original: messages['section.resizeAndCrop.presets.original'],
  square: messages['section.resizeAndCrop.presets.square'],
  'landscape-16:9': messages['section.resizeAndCrop.presets.landscape-16:9'],
  'landscape-4:3': messages['section.resizeAndCrop.presets.landscape-4:3'],
  'portrait-9:16': messages['section.resizeAndCrop.presets.portrait-9:16'],
  'portrait-3:4': messages['section.resizeAndCrop.presets.portrait-3:4'],
};

export const Presets: FC = () => {
  const { formatMessage } = useIntl();
  const { presetsOrder } = useOptions();
  const dispatch = useDispatch();

  const [presetsOrderBeforeDrop, setPresetsOrderBeforeDrop] = useState(presetsOrder);

  useEffect(() => {
    setPresetsOrderBeforeDrop(presetsOrder);
  }, [presetsOrder]);

  const onMove = (oldIndex: number, newIndex: number) => {
    setPresetsOrderBeforeDrop((prevPresetsOrderBeforeDrop) => {
      return move(prevPresetsOrderBeforeDrop, oldIndex, newIndex);
    });
  };

  const onDrop = useCallback(() => {
    dispatch(updateOptions({ presetsOrder: presetsOrderBeforeDrop }));
  }, [dispatch, presetsOrderBeforeDrop]);

  return (
    <div>
      <TextLine>
        <FormattedMessage {...messages['section.resizeAndCrop.presets']} />
      </TextLine>
      <DndProvider backend={HTML5Backend}>
        {presetsOrderBeforeDrop.map((presetName, index) => (
          <StyledDraggableWithHandle key={presetName} id={presetName} index={index} type="card" iconSize="lg" onMove={onMove} onDrop={onDrop}>
            {presetName === 'custom'
              ? <CustomPreset />
              : <PredefinedPreset label={formatMessage(presetNameToLabelMessage[presetName])} name={presetName} />
            }
          </StyledDraggableWithHandle>
        ))}
      </DndProvider>
    </div>
  );
};
